/**
 * Authentication Context and Provider
 * Manages user authentication state across the application.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, parseResponse } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Check for existing auth on mount
    useEffect(() => {
        const checkAuth = async () => {
            if (authAPI.isAuthenticated()) {
                try {
                    const response = await authAPI.getMe();
                    if (response.ok) {
                        const userData = await parseResponse(response);
                        setUser(userData);
                    }
                } catch (err) {
                    console.error('Auth check failed:', err);
                }
            }
            setLoading(false);
        };

        checkAuth();
    }, []);

    const login = useCallback(async (email, password) => {
        setError(null);
        try {
            const response = await authAPI.login(email, password);

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.detail || 'Login failed');
            }

            // Get user data
            const meResponse = await authAPI.getMe();
            if (meResponse.ok) {
                const userData = await parseResponse(meResponse);
                setUser(userData);
                return { success: true };
            }

            throw new Error('Failed to get user data');
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        }
    }, []);

    const register = useCallback(async (userData) => {
        setError(null);
        try {
            const response = await authAPI.register(userData);

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.detail || 'Registration failed');
            }

            return { success: true };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        }
    }, []);

    const logout = useCallback(async () => {
        await authAPI.logout();
        setUser(null);
    }, []);

    const hasRole = useCallback((role) => {
        if (!user) return false;
        return user.roles?.includes(role);
    }, [user]);

    const hasAnyRole = useCallback((roles) => {
        if (!user) return false;
        return roles.some(role => user.roles?.includes(role));
    }, [user]);

    const value = {
        user,
        loading,
        error,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        hasRole,
        hasAnyRole,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
